'use client';

import { FC, useEffect, useState } from 'react';
import {
  getSlicePadding,
  pwaButtonFields,
  resolveCtaVariant,
  textAlign,
  toCtaButtonPropsFromItem,
} from '@/utils';
import { Container, CtaButton } from '@/components';
import classNames from 'classnames';
import styles from './pwabutton.module.scss';
import {
  BeforeInstallPromptEvent,
  PwaButtonSlice,
  PwaButtonSliceDefaultPrimary,
} from '@/types';
import {
  clearDeferredInstallPrompt,
  getDeferredInstallPrompt,
  subscribeInstallPrompt,
} from './pwaInstallPrompt';

const buildPwaCta = (primary: PwaButtonSliceDefaultPrimary) => {
  const mapped = toCtaButtonPropsFromItem(primary, pwaButtonFields, 'primary');

  if (mapped) {
    return mapped;
  }

  const label = primary.button_text?.trim();

  if (!label) {
    return null;
  }

  return {
    label,
    variant: resolveCtaVariant({
      ctaType: primary.button_color,
      hasCustomColors: false,
      fallback: 'primary',
    }),
  };
};

const PwaButton: FC<PwaButtonSlice> = slice => {
  const marginBottom = slice?.primary?.bottom_spacing;
  const paddingSection = slice?.primary?.padding_section;
  const alignment = slice?.primary?.horizontal_position ?? 'center';
  const primary = slice.primary ?? {};
  const cta = buildPwaCta(primary);

  const [promptEvent, setPromptEvent] =
    useState<BeforeInstallPromptEvent | null>(() => getDeferredInstallPrompt());

  useEffect(() => subscribeInstallPrompt(setPromptEvent), []);

  if (!cta) {
    return null;
  }

  if (!promptEvent) {
    return null;
  }

  const handleClick = () => {
    promptEvent.prompt();
    clearDeferredInstallPrompt();
    setPromptEvent(null);
  };

  return (
    <Container
      as="section"
      className={classNames(styles.pwabutton, getSlicePadding(paddingSection), {
        ['spacing_bottom']: marginBottom,
      })}
      style={{ justifyContent: textAlign(alignment) }}
    >
      <CtaButton
        {...cta}
        className={classNames(styles.pwa_button__cta, {
          [styles.pwa_button__cta_fit]: cta.fitContent,
        })}
        onClick={handleClick}
      />
    </Container>
  );
};

export { PwaButton };
